import { useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { apiRequest } from "@/api/client";
import { SyncBadge, SyncProvider } from "./SyncContext";
import AuditorLegal from "./AuditorLegal";
import artegoIcon from "@/assets/artego-icon.png";

// Guscio dell'app da campo: manifest e service worker propri (PWA), cancello privacy, indicatore di sincronizzazione.
function usePwa() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/auditor-manifest.webmanifest");
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/auditor-sw.js", { scope: "/auditor/" }).then(() => navigator.serviceWorker.ready).then((reg) => {
        // dice al service worker quali file della build mettere da parte per l'uso senza rete
        const urls = performance.getEntriesByType("resource").map((r) => r.name).filter((u) => u.startsWith(window.location.origin) && /\/static\/|\.png$|\.webmanifest$/.test(u));
        if (reg.active) reg.active.postMessage({ type: "precache", urls: [...new Set(urls)] });
      }).catch(() => { /* senza service worker l'app funziona comunque online */ });
    }
    return () => { if (link && prev) link.setAttribute("href", prev); };
  }, []);
}

export default function AuditorShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(() => { try { return JSON.parse(localStorage.getItem("dvr_auditor_me") || "null"); } catch { return null; } });
  const [meError, setMeError] = useState(false);
  usePwa();

  useEffect(() => {
    if (!user || user.role !== "auditor_esterno") return;
    apiRequest("/auditor/me").then((m) => { setMe(m); localStorage.setItem("dvr_auditor_me", JSON.stringify(m)); setMeError(false); }).catch(() => setMeError(true));
  }, [user]);

  if (loading) return <div className="grid min-h-screen place-items-center bg-slate-50 text-lg">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "auditor_esterno") return <Navigate to="/" replace />;
  const out = () => { logout(); localStorage.removeItem("dvr_auditor_me"); navigate("/login"); };
  const needLegal = me && !me.gdpr_ok;

  return (
    <SyncProvider>
      <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="auditor-shell">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b bg-white px-4 py-3 shadow-sm">
          <img src={artegoIcon} alt="" className="h-9 w-9 rounded-md object-contain" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-heading text-base font-bold leading-tight">Audit sul campo</p>
            <p className="truncate text-xs text-slate-600">{user.name}</p>
          </div>
          <SyncBadge />
          <Button variant="ghost" size="icon" className="h-11 w-11" onClick={out} aria-label="Esci"><LogOut className="h-5 w-5" /></Button>
        </header>
        <main className="mx-auto max-w-2xl px-4 pb-32 pt-4">
          {meError && !me && <p className="rounded bg-amber-100 p-3 text-sm">Non riesco a verificare il tuo profilo (sei offline?). Riprova quando torna la rete.</p>}
          {needLegal ? <AuditorLegal onDone={(m) => { setMe(m); localStorage.setItem("dvr_auditor_me", JSON.stringify(m)); }} /> : me ? <Outlet /> : !meError && <p className="p-6 text-center text-slate-600">Caricamento…</p>}
        </main>
      </div>
    </SyncProvider>
  );
}
